// @flow
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import { Container } from 'reactstrap';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowHeader from 'components/molecules/AppointmentFlowHeader/';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import DeclinedServicesList from 'components/molecules/DeclinedServicesList';
import Alert from 'components/atoms/Alert';

// CTA Components
import CTAFlowHeader from 'components/molecules/CTAFlowHeader';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Constants
import {
  HOME_ROUTE,
  USER_VEHICLES_ROUTE,
  SERVICES_ROUTE,
  VEHICLE_ROUTE
} from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';
import { labelsStepIndicator } from 'constants/CTAConfig';

// Utils
import { getCTAbackButtonLabel } from 'utils/string';

// Types
import { gtmUserEventType } from 'types/gtm';
import { type Promotion } from 'api/xws/promotions/promotionsWs';
import type { CTA } from 'api/xws/optimizely/optimizely';

import './DeclinedServicesPage.scss';
import { resetScrollPosition } from 'utils/DOM';

const { FORWARD } = NAVIGATION;

const ns = 'declined-services';

type DeclinedService = {
  id: number,
  name: string,
  price: number,
  duration: number,
  opcode: string
};

type Props = {
  declinedServices: Array<DeclinedService>,
  deferredServices: Array<DeclinedService>,
  apptDeclinedServices: Array<DeclinedService>,
  apptDeferredServices: Array<number>,
  promotionGroup?: string,
  promotion?: Promotion,
  gtmUserEvent: gtmUserEventType,
  goTo: (path: string, direction: string) => void,
  showMileage: boolean,
  vehicleEditableEnabled: boolean,
  isLoggedIn: boolean,
  intl: IntlShape,
  ctaBanner: CTA | null,
  vehicle: Object,
  ctaBanner: number | null,
  showProgressBar: boolean,
  hideBottomCTA: boolean,
  ctaBackButton: string | null
};

class DeclinedServicesPage extends Component<Props> {
  componentWillMount() {
    resetScrollPosition(0);
  }

  gtmUserEventSkipDeclined() {
    const { gtmUserEvent } = this.props;
    gtmUserEvent(
      'GTM_CURRENT_PAGE',
      'Skipped',
      'GTM_CURRENT_PAGE Skipped',
      'GTM_CURRENT_PAGE'
    );
  }

  getPreviousRoute = () => {
    const { showMileage, vehicleEditableEnabled, isLoggedIn } = this.props;

    if (!showMileage) {
      if (isLoggedIn) {
        return USER_VEHICLES_ROUTE;
      }
      if (!vehicleEditableEnabled) {
        return HOME_ROUTE;
      }
    }
    return VEHICLE_ROUTE;
  };

  render() {
    const {
      goTo,
      declinedServices,
      apptDeclinedServices,
      apptDeferredServices,
      deferredServices,
      promotion,
      promotionGroup,
      intl,
      ctaBanner,
      gtmUserEvent,
      showProgressBar,
      hideBottomCTA,
      ctaBackButton,
      vehicle
    } = this.props;
    const servicesList = declinedServices.concat(deferredServices);
    const servicesSelected =
      apptDeclinedServices.length + apptDeferredServices.length > 0;
    const backRoute = this.getPreviousRoute();
    const label = intl.formatMessage({ id: 'c8.header.vehicle' });
    const backButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.return.to.previous.page',
      defaultMessage: 'Return to previous page'
    });
    const nextButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.continue.to.select.appointment.service',
      defaultMessage: 'Continue to select appointment service'
    });
    const { make, year, model } = vehicle;
    const headerVehicleLabel = `${make} · ${year} · ${model}`;
    const ctaLabel = getCTAbackButtonLabel(
      ctaBackButton,
      headerVehicleLabel,
      label,
      intl
    );
    return (
      <AppointmentFlowWizard
        className={ns}
        hideFooter={Boolean(hideBottomCTA)}
        header={() => {
          return ctaBanner !== null ? (
            <CTAFlowHeader
              label={ctaLabel}
              hideServices={false}
              routeTo={backRoute}
              backButtonAriaLabel={backButtonAriaLabel}
              nextButtonAriaLabel={nextButtonAriaLabel}
              ctaBanner={ctaBanner}
              onClickCtaButton={() => {
                !servicesSelected && this.gtmUserEventSkipDeclined();
                gtmUserEvent(
                  'NEXT button',
                  'Clicked',
                  `Go to next page through CTA`,
                  `GTM_CURRENT_PAGE > ${SERVICES_ROUTE} page`
                );
                goTo(SERVICES_ROUTE, FORWARD);
              }}
              typeButton={'cta-next'}
            />
          ) : (
            <AppointmentFlowHeader
              label={label}
              routeTo={backRoute}
              backButtonAriaLabel={backButtonAriaLabel}
            />
          );
        }}
        footer={() => {
          return !hideBottomCTA ? (
            <AppointmentFlowFooter
              buttonText={intl.formatMessage({
                id: servicesSelected ? 'c8.footer.next' : 'c8.footer.not.now'
              })}
              onClick={() => {
                !servicesSelected && this.gtmUserEventSkipDeclined();
                goTo(SERVICES_ROUTE, FORWARD);
              }}
              ariaLabel={nextButtonAriaLabel}
            />
          ) : null;
        }}
        body={() => (
          <React.Fragment>
            {showProgressBar && (
              <CtaStepIndicator
                htmlId="VehiclePageStepIndicator"
                labels={labelsStepIndicator(intl)}
                activeStep={2}
              />
            )}
            <Container>
              {promotion &&
                promotionGroup === 'declined' && (
                  <Alert color="warning" animation="top-down">
                    <FormattedMessage
                      id="c8.appointment.promotion.applied"
                      values={{ description: promotion.promotionDescription }}
                    />
                  </Alert>
                )}
              <h1 className={`${ns}__title`}>
                {intl.formatMessage({
                  id: 'c8.declined.services.page.title'
                })}
              </h1>
              <div className={`${ns}__label`}>
                {intl.formatMessage({
                  id: 'c8.declined.services.page.services.list.label'
                })}
              </div>
              <DeclinedServicesList serviceList={servicesList} />
            </Container>
          </React.Fragment>
        )}
      />
    );
  }
}

export default DeclinedServicesPage;



// WEBPACK FOOTER //
// ./src/components/pages/DeclinedServicesPage/DeclinedServicesPage.js